import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import JsBarcode from 'jsbarcode';

const BUILT_IN_SIZES = {
    '2.25x1.25': { label: '2.25" × 1.25" (Thermal)', mode: 'thermal', w: 57.15, h: 31.75 },
    '30up': {
        label: '30-up Sheet (3×10 per page)',
        mode: 'sheet',
        pageW: 215.9, pageH: 279.4,
        labelW: 66.675, labelH: 25.4,
        marginLeft: 4.762, marginTop: 12.7,
        colGap: 3.175, rowGap: 0,
        cols: 3, rows: 10,
    },
};

const MODES = { SINGLE: 'single', BULK: 'bulk' };
const LS_KEY = 'labelprinter_colmap';
const LS_CUSTOM_SIZES_KEY = 'labelprinter_custom_sizes';
const DEFAULT_COLS = { gtin: 1, title: 2, expiration: 3, qty: 4 };

function loadColMap() {
    try {
        const saved = localStorage.getItem(LS_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (['gtin', 'title', 'expiration', 'qty'].every(k => Number.isInteger(parsed[k]) && parsed[k] >= 1)) {
                return parsed;
            }
        }
    } catch { }
    return { ...DEFAULT_COLS };
}
function loadCustomSizes() {
    try {
        const saved = localStorage.getItem(LS_CUSTOM_SIZES_KEY);
        if (saved) return JSON.parse(saved);
    } catch { }
    return [];
}
function saveCustomSizes(sizes) {
    try { localStorage.setItem(LS_CUSTOM_SIZES_KEY, JSON.stringify(sizes)); } catch { }
}
function generateBarcodeDataUrl(value) {
    try {
        const canvas = document.createElement('canvas');
        const cleaned = String(value).replace(/\D/g, '');
        let format = 'CODE128';
        if (cleaned.length === 12) format = 'UPC';
        else if (cleaned.length === 13) format = 'EAN13';
        else if (cleaned.length === 14) format = 'CODE128';
        JsBarcode(canvas, cleaned, {
            format, displayValue: false, margin: 2,
            width: 4, height: 100,
            background: '#ffffff', lineColor: '#000000', flat: true,
        });
        return canvas.toDataURL('image/png');
    } catch (e) {
        return null;
    }
}

// initialItems: [{ gtin, title, expiration, quantity }] — when provided, the
// bulk view is pre-loaded from this data instead of requiring a paste step.
export default function LabelPrinter({ prefill = {}, initialItems = null }) {
    const [mode, setMode] = useState(initialItems && initialItems.length ? MODES.BULK : MODES.SINGLE);
    const [labelSize, setLabelSize] = useState('2.25x1.25');
    const [quantity, setQuantity] = useState(1);

    const [customSizes, setCustomSizes] = useState(loadCustomSizes);
    const [showCustomSizeModal, setShowCustomSizeModal] = useState(false);
    const [newSizeName, setNewSizeName] = useState('');
    const [newSizeW, setNewSizeW] = useState('');
    const [newSizeH, setNewSizeH] = useState('');
    const [newSizeError, setNewSizeError] = useState('');

    const [single, setSingle] = useState({
        gtin: prefill.gtin || '',
        title: prefill.title || '',
        expiration: prefill.expiration || '',
    });

    const [csvText, setCsvText] = useState('');
    const [csvRows, setCsvRows] = useState(
        initialItems && initialItems.length ? [] : []
    );
    const [csvParsed, setCsvParsed] = useState(false);
    const [parseError, setParseError] = useState('');

    // When initialItems are provided, skip paste entirely — show a selectable
    // image checklist instead, defaulted to "all selected".
    const [selectedIndices, setSelectedIndices] = useState(
        () => new Set((initialItems || []).map((_, i) => i))
    );
    const hasInitialItems = !!(initialItems && initialItems.length);

    // Per-item label quantity — defaults to the quantity-to-ship value already
    // captured on the batch item, editable per row from here.
    const [itemQuantities, setItemQuantities] = useState(
        () => (initialItems || []).map((it) => Math.max(1, parseInt(it.quantity, 10) || 1))
    );

    const toggleItem = (i) => {
        setSelectedIndices((prev) => {
            const next = new Set(prev);
            next.has(i) ? next.delete(i) : next.add(i);
            return next;
        });
    };
    const selectAll = () => setSelectedIndices(new Set((initialItems || []).map((_, i) => i)));
    const clearAll = () => setSelectedIndices(new Set());
    const setItemQuantity = (i, val) => {
        const n = Math.max(1, Math.min(500, parseInt(val, 10) || 1));
        setItemQuantities((prev) => prev.map((q, idx) => (idx === i ? n : q)));
    };

    const [colMap, setColMap] = useState(loadColMap);
    const [showColConfig, setShowColConfig] = useState(false);
    const [draftCols, setDraftCols] = useState({ ...DEFAULT_COLS });

    useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(colMap)); } catch { } }, [colMap]);
    useEffect(() => { saveCustomSizes(customSizes); }, [customSizes]);

    const allSizes = {
        ...BUILT_IN_SIZES,
        ...Object.fromEntries(
            customSizes.map(s => [
                `custom_${s.id}`,
                { label: `${s.name} (${s.w}" × ${s.h}")`, mode: 'thermal', w: s.w * 25.4, h: s.h * 25.4, isCustom: true, customId: s.id }
            ])
        ),
        'add_custom': { label: '＋ Add custom size...', mode: null },
    };

    const getSizeCfg = () => {
        if (labelSize === 'add_custom') return allSizes['2.25x1.25'];
        return allSizes[labelSize] || BUILT_IN_SIZES['2.25x1.25'];
    };

    const handleSizeChange = (val) => {
        if (val === 'add_custom') {
            setNewSizeName(''); setNewSizeW(''); setNewSizeH(''); setNewSizeError('');
            setShowCustomSizeModal(true);
        } else {
            setLabelSize(val);
        }
    };

    const saveNewCustomSize = () => {
        const w = parseFloat(newSizeW);
        const h = parseFloat(newSizeH);
        const name = newSizeName.trim();
        if (!name) return setNewSizeError('Please enter a name.');
        if (!w || w <= 0 || w > 24) return setNewSizeError('Width must be between 0.1 and 24 inches.');
        if (!h || h <= 0 || h > 24) return setNewSizeError('Height must be between 0.1 and 24 inches.');
        const id = Date.now().toString();
        setCustomSizes([...customSizes, { id, name, w, h }]);
        setLabelSize(`custom_${id}`);
        setShowCustomSizeModal(false);
    };

    const deleteCustomSize = (id) => {
        setCustomSizes(customSizes.filter(s => s.id !== id));
        if (labelSize === `custom_${id}`) setLabelSize('2.25x1.25');
    };

    const openColConfig = () => { setDraftCols({ ...colMap }); setShowColConfig(true); };
    const saveColConfig = () => {
        const valid = Object.values(draftCols).every(v => Number.isInteger(v) && v >= 1);
        if (!valid) return alert('All column numbers must be 1 or higher.');
        setColMap({ ...draftCols });
        setShowColConfig(false);
        if (csvParsed && !initialItems) setCsvParsed(false);
    };

    const formatExpiry = (val) => {
        if (!val) return '';
        const match = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match) return `${match[2]}-${match[3]}-${match[1]}`;
        return val;
    };

    const parseCSV = () => {
        setParseError('');
        const lines = csvText.trim().split('\n').filter(l => l.trim());
        if (!lines.length) return setParseError('Paste at least one row of data.');
        const parseLine = (line) => line.includes('\t') ? line.split('\t') : line.split(',').map(c => c.replace(/^"|"$/g, '').trim());
        const allRows = lines.map(parseLine);
        const gi = colMap.gtin - 1;
        const dataRows = allRows.filter(row => /\d{6,}/.test(row[gi] || ''));
        if (!dataRows.length) return setParseError(`No valid rows found. Column ${colMap.gtin} should contain the GTIN/barcode (6+ digits).`);
        setCsvRows(dataRows);
        setCsvParsed(true);
    };

    const buildLabels = () => {
        if (mode === MODES.SINGLE) return Array(Math.max(1, quantity)).fill({ ...single });

        if (hasInitialItems) {
            return initialItems
                .map((it, i) => ({ it, i }))
                .filter(({ i }) => selectedIndices.has(i))
                .flatMap(({ it, i }) => {
                    const rowQty = itemQuantities[i] || 1;
                    return Array(rowQty).fill({ gtin: it.gtin || '', title: it.title || '', expiration: it.expiration || '' });
                });
        }

        const gi = colMap.gtin - 1, ti = colMap.title - 1, ei = colMap.expiration - 1, qi = colMap.qty - 1;
        return csvRows.flatMap(row => {
            const qtyRaw = String(row[qi] || '').replace(/\D/g, '');
            const rowQty = qtyRaw.length > 0 ? Math.min(500, Math.max(1, parseInt(qtyRaw, 10))) : Math.max(1, quantity);
            const item = { gtin: (row[gi] || '').trim(), title: (row[ti] || '').trim(), expiration: (row[ei] || '').trim() };
            return Array(rowQty).fill({ ...item });
        });
    };

    const drawThermalLabel = (doc, label, x, y, w, h) => {
        doc.setFillColor(255, 255, 255); doc.rect(x, y, w, h, 'F');
        const pad = 2, inner = w - pad * 2;
        const barcodeDataUrl = generateBarcodeDataUrl(label.gtin);
        const barcodeH = h * 0.45, barcodeY = y + pad;
        if (barcodeDataUrl) doc.addImage(barcodeDataUrl, 'PNG', x + 3, barcodeY, w - 6, barcodeH);
        const gtinY = barcodeY + barcodeH + 2.5;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(0, 0, 0);
        doc.text(label.gtin || '', x + w / 2, gtinY, { align: 'center' });
        const titleY = gtinY + 3.5;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(30, 30, 30);
        const titleLines = doc.splitTextToSize(label.title || '', inner);
        const maxTitleLines = (w < 57.15 || h < 31.75) ? 1 : 2;
        doc.text(titleLines.slice(0, maxTitleLines), x + w / 2, titleY, { align: 'center' });
        if (label.expiration) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(0, 0, 0);
            doc.text(`EXPIRY DATE ${formatExpiry(label.expiration)}`, x + w / 2, y + h - pad - 1, { align: 'center' });
        }
    };

    const drawSheetLabel = (doc, label, x, y, w, h) => {
        doc.setFillColor(255, 255, 255); doc.rect(x, y, w, h, 'F');
        const pad = 1.5, inner = w - pad * 2;
        const barcodeDataUrl = generateBarcodeDataUrl(label.gtin);
        const barcodeH = h * 0.42, barcodeY = y + pad;
        if (barcodeDataUrl) doc.addImage(barcodeDataUrl, 'PNG', x + 2, barcodeY, w - 4, barcodeH);
        const gtinY = barcodeY + barcodeH + 2;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(0, 0, 0);
        doc.text(label.gtin || '', x + w / 2, gtinY, { align: 'center' });
        const titleY = gtinY + 3;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5); doc.setTextColor(30, 30, 30);
        const titleLines = doc.splitTextToSize(label.title || '', inner);
        doc.text(titleLines.slice(0, 2), x + w / 2, titleY, { align: 'center' });
        if (label.expiration) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(0, 0, 0);
            doc.text(`EXPIRY DATE ${formatExpiry(label.expiration)}`, x + w / 2, y + h - pad - 0.5, { align: 'center' });
        }
    };

    const printLabels = (labels, sizeCfg, filename) => {
        if (sizeCfg.mode === 'thermal') {
            const { w: lw, h: lh } = sizeCfg;
            const doc = new jsPDF({ unit: 'mm', format: [lh, lw], orientation: 'landscape' });
            labels.forEach((label, i) => {
                if (i > 0) doc.addPage([lh, lw], 'landscape');
                drawThermalLabel(doc, label, 0, 0, lw, lh);
            });
            doc.save(filename);
        } else {
            const { pageW, pageH, labelW, labelH, marginLeft, marginTop, colGap, rowGap, cols, rows } = sizeCfg;
            const perPage = cols * rows;
            const doc = new jsPDF({ unit: 'mm', format: [pageW, pageH], orientation: 'portrait' });
            labels.forEach((label, i) => {
                const pos = i % perPage;
                if (i > 0 && pos === 0) doc.addPage([pageW, pageH], 'portrait');
                const col = pos % cols, row = Math.floor(pos / cols);
                drawSheetLabel(doc, label, marginLeft + col * (labelW + colGap), marginTop + row * (labelH + rowGap), labelW, labelH);
            });
            doc.save(filename);
        }
    };

    const generatePDF = () => {
        const labels = buildLabels();
        if (!labels.length) return alert('No labels to print.');
        const missingGtin = labels.find(l => !l.gtin || !String(l.gtin).trim());
        if (missingGtin) return alert('No barcode available for one or more items. Please add a UPC/GTIN before printing.');
        printLabels(labels, getSizeCfg(), 'labels.pdf');
    };

    const generateExpiryOnlyPDF = () => {
        const expiryList = mode === MODES.SINGLE
            ? (single.expiration ? [single.expiration] : [])
            : [...new Set(buildLabels().map(l => l.expiration).filter(Boolean))];
        if (!expiryList.length) return alert('No expiration date to print.');
        const sizeCfg = getSizeCfg();
        const lw = sizeCfg.mode === 'thermal' ? sizeCfg.w : sizeCfg.labelW;
        const lh = sizeCfg.mode === 'thermal' ? sizeCfg.h : sizeCfg.labelH;
        const doc = new jsPDF({ unit: 'mm', format: [lh, lw], orientation: 'landscape' });
        expiryList.forEach((exp, i) => {
            if (i > 0) doc.addPage([lh, lw], 'landscape');
            doc.setFillColor(255, 255, 255); doc.rect(0, 0, lw, lh, 'F');
            doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(80, 80, 80);
            doc.text('EXPIRY DATE', lw / 2, lh * 0.32, { align: 'center' });
            const formatted = formatExpiry(exp);
            doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
            const textW = doc.getTextWidth(formatted);
            if (textW > lw * 0.88) doc.setFontSize(18 * (lw * 0.88 / textW));
            doc.setTextColor(0, 0, 0);
            doc.text(formatted, lw / 2, lh * 0.62, { align: 'center' });
        });
        doc.save('expiry-labels.pdf');
    };

    const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', boxSizing: 'border-box', outline: 'none', backgroundColor: 'white' };
    const labelStyle = { fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '3px', display: 'block' };
    const fieldWrap = { marginBottom: '10px' };
    const iconBtn = { padding: '4px 8px', borderRadius: '5px', fontSize: '11px', fontWeight: '600', border: '1px solid #d1d5db', background: '#f3f4f6', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: '1.4' };
    const sizeCfg = getSizeCfg();

    return (
        <div style={{ padding: '14px', backgroundColor: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb' }}>

            {showCustomSizeModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
                    <div style={{ background: 'white', borderRadius: '10px', padding: '20px', width: '300px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px', color: '#111827' }}>Add Custom Label Size</div>
                        <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '14px' }}>Sizes are saved and will appear in the dropdown for future use.</div>
                        <div style={fieldWrap}>
                            <label style={labelStyle}>Name (e.g. "4×6 Shipping")</label>
                            <input style={inputStyle} value={newSizeName} onChange={e => setNewSizeName(e.target.value)} placeholder="My custom size" autoFocus />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Width (inches)</label>
                                <input style={inputStyle} type="number" min="0.1" max="24" step="0.25" value={newSizeW} onChange={e => setNewSizeW(e.target.value)} placeholder="e.g. 2" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Height (inches)</label>
                                <input style={inputStyle} type="number" min="0.1" max="24" step="0.25" value={newSizeH} onChange={e => setNewSizeH(e.target.value)} placeholder="e.g. 1" />
                            </div>
                        </div>
                        {newSizeError && <div style={{ fontSize: '11px', color: '#dc2626', marginBottom: '8px' }}>{newSizeError}</div>}
                        {customSizes.length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                                <div style={{ fontSize: '10px', fontWeight: '600', color: '#6b7280', marginBottom: '6px' }}>SAVED SIZES</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {customSizes.map(s => (
                                        <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px', background: '#f9fafb', borderRadius: '5px', border: '1px solid #e5e7eb', fontSize: '11px' }}>
                                            <span style={{ color: '#374151', fontWeight: '500' }}>{s.name} <span style={{ color: '#9ca3af' }}>({s.w}" × {s.h}")</span></span>
                                            <button onClick={() => deleteCustomSize(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '13px', padding: '0 2px', lineHeight: 1 }} title="Delete">×</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => setShowCustomSizeModal(false)} style={{ flex: 1, padding: '8px', borderRadius: '6px', fontSize: '12px', border: '1px solid #d1d5db', background: '#f3f4f6', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={saveNewCustomSize} style={{ flex: 1, padding: '8px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', background: '#2563eb', color: 'white', cursor: 'pointer' }}>Save & Use</button>
                        </div>
                    </div>
                </div>
            )}

            {showColConfig && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: '10px', padding: '18px', width: '280px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                        <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '12px', color: '#111827' }}>⚙️ Column Numbers</div>
                        <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '12px' }}>Enter the column number (1 = first column) for each field in your pasted data.</div>
                        {[{ key: 'gtin', label: 'GTIN / Barcode' }, { key: 'title', label: 'Product Title' }, { key: 'expiration', label: 'Expiration Date' }, { key: 'qty', label: 'Quantity' }].map(({ key, label }) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <label style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>{label}</label>
                                <input type="number" min={1} max={20} value={draftCols[key]} onChange={e => setDraftCols(p => ({ ...p, [key]: Math.max(1, parseInt(e.target.value) || 1) }))} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} />
                            </div>
                        ))}
                        <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                            <button onClick={() => setShowColConfig(false)} style={{ flex: 1, padding: '8px', borderRadius: '6px', fontSize: '12px', border: '1px solid #d1d5db', background: '#f3f4f6', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={saveColConfig} style={{ flex: 1, padding: '8px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', background: '#2563eb', color: 'white', cursor: 'pointer' }}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ fontWeight: '700', fontSize: '14px', color: '#111827', marginBottom: '12px' }}>Label Printer</div>

            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                {[MODES.SINGLE, MODES.BULK].map(m => (
                    <button key={m} onClick={() => setMode(m)} style={{ flex: 1, padding: '7px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer', background: mode === m ? 'linear-gradient(90deg,#14b8a6,#2563eb)' : '#e5e7eb', color: mode === m ? 'white' : '#374151' }}>
                        {m === MODES.SINGLE ? '📦 Single' : '📋 Batch Items'}
                    </button>
                ))}
            </div>

            {mode === MODES.SINGLE && (
                <div>
                    <div style={fieldWrap}>
                        <label style={labelStyle}>UPC / GTIN</label>
                        <input style={inputStyle} value={single.gtin} onChange={e => setSingle(p => ({ ...p, gtin: e.target.value }))} placeholder="e.g. 0673419419536" />
                    </div>
                    <div style={fieldWrap}>
                        <label style={labelStyle}>Product Title</label>
                        <input style={inputStyle} value={single.title} onChange={e => setSingle(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Fuggler Fuggalicious 9 inch" />
                    </div>
                    <div style={fieldWrap}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                            <label style={{ ...labelStyle, marginBottom: 0 }}>Expiration Date <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                            <button onClick={generateExpiryOnlyPDF} style={iconBtn}>🗓️ Expiry label</button>
                        </div>
                        <input style={inputStyle} type="date" value={single.expiration} onChange={e => setSingle(p => ({ ...p, expiration: e.target.value }))} />
                    </div>
                </div>
            )}

            {mode === MODES.BULK && (
                <div>
                    {hasInitialItems ? (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <div style={{ fontSize: '11px', color: '#374151', fontWeight: '600' }}>
                                    {selectedIndices.size} of {initialItems.length} selected
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button onClick={selectAll} style={iconBtn}>Select All</button>
                                    <button onClick={clearAll} style={iconBtn}>Clear</button>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '260px', overflowY: 'auto', marginBottom: '10px' }}>
                                {initialItems.map((it, i) => {
                                    const checked = selectedIndices.has(i);
                                    return (
                                        <label key={i} style={{
                                            display: 'flex', alignItems: 'center', gap: '10px', padding: '6px',
                                            border: `1px solid ${checked ? '#5eead4' : '#f3f4f6'}`,
                                            background: checked ? '#f0fdfa' : 'white',
                                            borderRadius: '6px', cursor: 'pointer',
                                        }}>
                                            <input type="checkbox" checked={checked} onChange={() => toggleItem(i)} />
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '5px', overflow: 'hidden',
                                                border: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                {it.image_url ? (
                                                    <img src={it.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <span style={{ fontSize: '14px' }}>📦</span>
                                                )}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: '11px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {it.title || 'Untitled'}
                                                </div>
                                                <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                                                    {it.gtin || 'No barcode'}{it.expiration ? ` · exp ${it.expiration}` : ''}
                                                </div>
                                            </div>
                                            <div onClick={(e) => e.preventDefault()} style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                                <span style={{ fontSize: '9px', color: '#9ca3af' }}>Qty</span>
                                                <input
                                                    type="number" min={1} max={500}
                                                    value={itemQuantities[i] ?? 1}
                                                    onChange={(e) => setItemQuantity(i, e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ width: '48px', padding: '4px 6px', fontSize: '11px', borderRadius: '5px', border: '1px solid #d1d5db', textAlign: 'center' }}
                                                />
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>

                            <button onClick={generateExpiryOnlyPDF} style={{ ...iconBtn, width: '100%', padding: '7px', marginBottom: '4px' }}>
                                🗓️ Generate Expiry Labels
                            </button>
                        </div>
                    ) : !csvParsed ? (
                        <div style={fieldWrap}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <div style={{ fontSize: '10px', color: '#6b7280' }}>
                                    GTIN col <strong>{colMap.gtin}</strong> · Title col <strong>{colMap.title}</strong> · Exp col <strong>{colMap.expiration}</strong> · Qty col <strong>{colMap.qty}</strong>
                                </div>
                                <button onClick={openColConfig} style={iconBtn}>⚙️ Columns</button>
                            </div>
                            <label style={labelStyle}>Paste rows (no header needed)</label>
                            <textarea style={{ ...inputStyle, height: '110px', resize: 'vertical', fontFamily: 'monospace', fontSize: '11px' }} value={csvText} onChange={e => { setCsvText(e.target.value); setParseError(''); }} placeholder={"00850036766056\tLego Minifigures\t2030-12-31\t3"} />
                            {parseError && <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px' }}>{parseError}</div>}
                            <button onClick={parseCSV} style={{ marginTop: '6px', width: '100%', padding: '8px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Parse Data →</button>
                        </div>
                    ) : (
                        <div>
                            <div style={{ fontSize: '11px', color: '#059669', fontWeight: '600', marginBottom: '6px' }}>✅ {csvRows.length} item{csvRows.length === 1 ? '' : 's'} ready to print</div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <button onClick={() => { setCsvParsed(false); setParseError(''); setCsvText(''); }} style={{ fontSize: '11px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>← Paste different data</button>
                                <button onClick={generateExpiryOnlyPDF} style={iconBtn}>🗓️ Expiry labels only</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '10px', paddingTop: '10px' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <div style={{ flex: 2 }}>
                        <label style={labelStyle}>Label Size</label>
                        <select style={inputStyle} value={labelSize} onChange={e => handleSizeChange(e.target.value)}>
                            {Object.entries(allSizes).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                </div>

                {labelSize.startsWith('custom_') && (() => {
                    const id = labelSize.replace('custom_', '');
                    const size = customSizes.find(s => s.id === id);
                    return size ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '10px', color: '#6b7280', marginBottom: '8px', padding: '4px 8px', background: '#f9fafb', borderRadius: '5px', border: '1px solid #e5e7eb' }}>
                            <span>Custom: {size.w}" × {size.h}"</span>
                            <button onClick={() => deleteCustomSize(id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '11px', fontWeight: '600' }}>Delete size</button>
                        </div>
                    ) : null;
                })()}

                {sizeCfg.mode === 'sheet' && (
                    <div style={{ fontSize: '10px', color: '#1d4ed8', marginBottom: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '5px', padding: '5px 8px' }}>
                        📄 8.5"×11" sheet — 3 cols × 10 rows (30 labels/page).
                    </div>
                )}

                <button onClick={generatePDF} style={{ width: '100%', padding: '11px', background: 'linear-gradient(90deg,#14b8a6,#2563eb)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(20,184,166,0.3)' }}>
                    🖨️ {mode === MODES.BULK && hasInitialItems ? 'Generate Labels' : 'Generate & Download PDF'}
                </button>
            </div>
        </div>
    );
}